import { addMonths, differenceInDays } from 'date-fns';
import React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { fetchDateRange } from '../utils/api/reports/analytics-requests';
import { DashboardChartHeader } from './components/chart-header';
import { DashboardDatePicker } from './components/dashboard-date-picker';
import { Bar } from '@nivo/bar';

function formatData(data) {
  if (data && Object.keys(data)) {
    return Object.entries(data).map((raw) => {
      return {
        time_range: raw[0],
        ...raw[1],
      };
    });
  }
  return [];
}

export function AssignmentsOnTimeChart(props) {
  const labels = {
    open: 'Open',
    overdue: 'Open Overdue',
    completed_on_time: 'Done, On Time',
    completed_overdue: 'Done, Overdue',
  };

  const defaultDateRange = [addMonths(new Date(), -3), new Date()];
  const [dateRange, setDateRange] = React.useState(defaultDateRange);
  const [data, setData] = React.useState([]);
  const [dateRangeError, setDateRangeError] = React.useState('');
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      if (!dateRange) {
        // If the date range is empty, force it to the default date range and do it again.
        setDateRange(defaultDateRange);
        return;
      }
      const daysDiff = differenceInDays(dateRange[1], dateRange[0]);
      if (daysDiff > 732) {
        setDateRangeError('Date range too large.  Please select a range of 2 years or less.');
        return;
      }
      setDateRangeError('');
      const jsonData = await fetchDateRange(
        props.reportClient,
        '/api/v1/analytics/assigned-status-due-counts',
        dateRange
      );
      const formattedData = formatData(jsonData);
      setData(formattedData);

      // We delay showing the datepicker until the rest of the UI is ready in order 
      // to avoid a snapping in the UI as it resizes itself.
      setShowDatePicker(true);
    })();
  }, [dateRange]);

  return (
    <div className="chart-box-flow">
      <div className="chart-box-flow-header">
        <DashboardChartHeader
          title="Assignment Completions"
          tooltip="A breakdown of due date assignment completions."
        />
        <DashboardDatePicker {...{ showDatePicker, dateRange, setDateRange, dateLocale: props.dateLocale }} />
      </div>
      {dateRangeError && <div className="ui message chart-date-error">{dateRangeError}</div>}
      <div className="chart-box-autosizer-wrapper">
        <AutoSizer>
          {({ width, height }) => (
            <Bar
              data={data}
              width={width}
              height={height}
              layout="horizontal"
              layers={[
                "grid",
                "axes",
                "bars",
                "markers",
                "legends",
                "annotations",
              ]}
              enableGridX={true}
              enableGridY={false}
              keys={['completed_on_time', 'completed_overdue', 'open', 'overdue']}
              indexBy="time_range"
              margin={{ left: 70, bottom: 20, top: 40, right: 30 }}
              padding={0.5}
              innerPadding={1}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              // colors={{ scheme: 'paired' }}
              colors={["#FEA58A", "#BEA7EC", "#4AD3DC", "#F8C98D", "#EA91AD"]}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Completion Statuses',
                legendPosition: 'middle',
                legendOffset: 32,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Days Since Assigned',
                legendPosition: 'middle',
                legendOffset: -60,
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor={{
                from: 'color',
                modifiers: [['darker', 1.6]],
              }}
              legendLabel={(datum) => `${labels[datum.id]}`}
              tooltipLabel={(datum) => `${labels[datum.id]} - ${datum.indexValue}`}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'top',
                  direction: 'row',
                  justify: false,
                  translateX: 0,
                  translateY: -40,
                  itemsSpacing: 0,
                  itemWidth: 110,
                  itemHeight: 18,
                  itemTextColor: '#999',
                  itemDirection: 'left-to-right',
                  itemOpacity: 1,
                  symbolSize: 8,
                  symbolShape: 'circle',
                  toggleSerie: true,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
              theme={{
                axis: {
                  domain: {
                    // line: { stroke: '#77777777', strokeWidth: 0.5 }
                  },
                  legend: {
                    text: { fill: "#999", fontSize: 10, fontWeight: "bold" }
                  },
                  ticks: {
                    line: { stroke: '#77777722', strokeWidth: 0.5 },
                    text: { fill: "#999", fontSize: 10 }
                  },
                },
                grid: {
                  line: {
                    stroke: '#77777722',
                    strokeWidth: 0.5
                  }
                },
                labels: {
                  text: { fontSize: 10, fontWeight: "bold" }
                },
                legends: {
                  text: { fontSize: 10, fontWeight: "bold" }
                }
              }}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  );
}
